/**
 * THE SELLER'S BROKER / CONSTRUCTION LICENSE LINE, BY DIVISION AND STATE (2026-10-23).
 *
 * Katy (Teams 10-22): "the built disclaimer still shows [Seller's Broker] and [Construction
 * Licenses] as placeholders." This fills them.
 *
 * Source: the legal Disclaimer Builder's own line ("LICENSE INFORMATION WHERE REQUIRED (from
 * Division)", Creative Studio › Disclaimer Builder) — one line per division — split by state
 * with the per-state rules in Creative Studio › Divisions (GA = N/A, TN = broker line, …),
 * because a line is a state requirement and several divisions sell in two or three states.
 * Tested against 1,198 sent eblasts (Sep 21 – Oct 21, 2026): when a job used the builder, the
 * email shipped its exact line 87% of the time. 15 of the 65 rows did not match what ships;
 * those are set to the line most sent and marked `src: "sent"` until Digital Ops rules on them
 * (simulation and the row list: ech/spec/sim-broker-license-20261023).
 *
 * The data lives in disclaimer-license-key.json. Pure functions only — no React, no network.
 */
import KEY from "./disclaimer-license-key.json";

interface KeyFile {
  version: string;
  states: Record<string, string[]>;
  markets: Record<string, string>;
  lines: Record<string, { text: string; src: string }>;
  /** the Creative Studio › Divisions text as read when the key was built */
  airtable: Record<string, { seller: string; construction: string }>;
}
const K = KEY as unknown as KeyFile;
export const LICENSE_KEY_VERSION = K.version;

const up = (s: string) => String(s || "").trim().toUpperCase();
const isState = (s: string) => /^[A-Z]{2}$/.test(s);

/** /refdata `license` — the division's raw Creative Studio › Divisions text (backend 2026-10-23) */
export interface LiveLicense { seller?: string; construction?: string; states?: string }

export interface RefdataLike {
  ids?: { communities?: Record<string, string>; masterplans?: Record<string, string> };
  addr?: { communities?: Record<string, { state?: string }>; masterplans?: Record<string, { state?: string }> };
  license?: LiveLicense;
}

/* ── ADAPTIVE: AN EDIT IN AIRTABLE WINS, AN UNTOUCHED CELL DOES NOT (2026-10-23) ─────────
   Creative Studio › Divisions holds each division's seller / construction text by state, and
   Digital Ops can edit it. But today's cells are out of date (45% right against the 1,198
   sends, vs 84% for the bundled key), so reading them blindly would make things worse. The
   key therefore keeps a copy of what the cells said when it was built, and the comparison is
   made PER STATE AND PER HALF (seller / construction): only the part Digital Ops actually
   changed replaces the bundled line; an untouched part — even inside an edited cell — never
   brings its stale Airtable text into the email. Details in `lineFor`. */
const squash = (s?: string) => String(s || "").replace(/[‘’]/g, "'").replace(/[“”]/g, '"').replace(/[–—]/g, "-").replace(/\s+/g, " ").trim();
const str = (v: unknown) => (typeof v === "string" ? v : v == null ? "" : String(v));

/* a list of states: "SC", "SC & NC", "SC, NC", "SC/NC", "SC and NC" */
const STATES = "[A-Z]{2}(?:\\s*(?:&|,|\\/|and)\\s*[A-Z]{2})*";
/* "N/A", "n/a", "NA", "N/A.", "None" — the state needs no line */
const NONE = /^(?:n\/?a|none)\.?$/i;
/* what follows a state list: "= N/A", ": N/A", "- ??", or the start of the text itself */
const AFTER = "(?:\\s*[:=\\-–]\\s*((?:[Nn]\\/?[Aa]|[Nn]one)\\.?|\\?\\?)(?=\\s|$)|\\s*[:\\-–]?\\s+(?=[Ss]eller|[Cc]onstruction|[Bb]roker))";

/** "GA = N/A\nTN Seller's Broker: …" → per-state text; "" = none required; "?" = unknown */
export function parseStateText(text: string, states: string[]): Record<string, string> {
  const out: Record<string, string[]> = {};
  const flag: Record<string, string> = {};
  states.forEach((s) => { out[s] = []; });
  /* a state prefix after a sentence starts its own line — no lookbehind (older Safari) */
  const t = str(text).replace(/\r/g, "")
    .replace(new RegExp("\\.[ \\t]+(?=" + STATES + AFTER + ")", "g"), ".\n").trim();
  if (!t) { states.forEach((s) => { flag[s] = "?"; }); }
  else {
    const re = new RegExp("(?:^|\\n)(" + STATES + ")" + AFTER, "g");
    const ms: { start: number; end: number; sts: string[]; val?: string }[] = [];
    let m: RegExpExecArray | null;
    while ((m = re.exec(t))) ms.push({ start: m.index, end: m.index + m[0].length, sts: m[1].split(/\s*(?:&|,|\/|and)\s*/).map((x) => x.trim()).filter(isState), val: m[2] });
    if (!ms.length) {
      const v = t.trim();
      states.forEach((s) => {
        if (NONE.test(v)) flag[s] = "";
        else if (v.indexOf("??") >= 0) flag[s] = "?";
        /* text with no state in front only says which state it is for when there is ONE */
        else if (states.length === 1) out[s].push(v);
        else flag[s] = "?";
      });
    } else {
      const head = t.slice(0, ms[0].start).trim();
      /* unprefixed text before the first state: only meaningful in a one-state division */
      if (head && states.length === 1) states.forEach((s) => out[s].push(head));
      else if (head) states.forEach((s) => { flag[s] = "?"; });
      ms.forEach((x, i) => {
        const seg = t.slice(x.end, i + 1 < ms.length ? ms[i + 1].start : t.length).trim();
        x.sts.forEach((s) => {
          if (x.val) flag[s] = NONE.test(x.val) ? "" : "?";
          else { (out[s] = out[s] || []).push(seg); }
        });
      });
    }
  }
  const res: Record<string, string> = {};
  Object.keys(out).concat(Object.keys(flag)).forEach((s) => {
    const v = (out[s] || []).join(" ").replace(/\s+/g, " ").trim();
    if (flag[s] === "?") { res[s] = "?"; return; }
    res[s] = v ? (v.indexOf("??") >= 0 ? "?" : NONE.test(v) ? "" : v) : (s in flag ? flag[s] : "?");
  });
  return res;
}

/** the states a division's text should be read against: the live list plus the key's */
function statesFor(d: string, live?: LiveLicense | null): string[] {
  const sts = str(live && live.states).split(/[,\n;/&]+/).map(up).filter(isState).concat(K.states[d] || []);
  return sts.filter((x, i) => sts.indexOf(x) === i);
}

/** per-half edit check for one state: which half Digital Ops changed since the key was built */
function editsFor(d: string, s: string, live: LiveLicense) {
  const uniq = statesFor(d, live);
  const lv = { seller: parseStateText(str(live.seller), uniq)[s], constr: parseStateText(str(live.construction), uniq)[s] };
  const snap = K.airtable[d];
  if (!snap) return { lv, sn: null, seller: true, constr: true };
  const sn = { seller: parseStateText(str(snap.seller), uniq)[s], constr: parseStateText(str(snap.construction), uniq)[s] };
  return { lv, sn, seller: squash(lv.seller) !== squash(sn.seller), constr: squash(lv.constr) !== squash(sn.constr) };
}

/** true when Digital Ops has changed any of this division's Airtable text since the key was built */
export function liveLicenseEdited(division: string, live?: LiveLicense | null): boolean {
  if (!live) return false;
  const d = up(division);
  const snap = K.airtable[d];
  if (!snap) return !!(squash(str(live.seller)) || squash(str(live.construction)));
  return squash(str(live.seller)) !== squash(snap.seller) || squash(str(live.construction)) !== squash(snap.construction);
}

/** the bundled line split at its "Construction License" label, or null when it has none */
function splitBundled(text: string): { seller: string; constr: string } | null {
  const i = text.search(/\bConstruction License/i);
  if (i < 0) return null;
  return { seller: text.slice(0, i).trim(), constr: text.slice(i).trim() };
}

/**
 * The line for ONE state.
 *   · nothing changed for this state since the key was built → the bundled line;
 *   · both halves changed → the live text, if both are known (a real line or an explicit N/A);
 *   · one half changed → that half from Airtable, the other from the bundled line. When the
 *     untouched half is "N/A" the bundled line is all the edited half; when the edited half
 *     used to be "N/A" the bundled line is all the untouched half; otherwise it is split
 *     at its "Construction License" label. A bundled line with no such label (the California,
 *     Nevada, Arizona and Maryland lines, whose Airtable construction cells are real lines)
 *     can't be split safely, so it stays whole.
 * A half that reads "??" or blank is unknown, so the bundled line stays — no edit can drop a
 * license from the email (Farhan's review of #396, 2026-10-23).
 * `undefined` = the key has no row for this state at all.
 */
function lineFor(d: string, s: string, live?: LiveLicense | null): string | undefined {
  const row = K.lines[d + "|" + s];
  if (!row) {
    /* no bundled line for this state: only a complete Airtable entry for it can fill one */
    if (!live || !liveLicenseEdited(d, live)) return undefined;
    const n = editsFor(d, s, live).lv;
    if (n.seller === "?" || n.constr === "?") return undefined;
    return [n.seller, n.constr].filter(Boolean).join(" ").trim();
  }
  const bundled = str(row.text).trim();
  if (!live || !liveLicenseEdited(d, live)) return bundled;
  const e = editsFor(d, s, live);
  if (!e.seller && !e.constr) return bundled;
  let seller: string, constr: string;
  if (e.seller && e.constr) { seller = e.lv.seller; constr = e.lv.constr; }
  else {
    /* the untouched half is "none required" (N/A, unchanged) → the bundled line is all the
       other half, so the edit replaces it whole (Atlanta's TN broker line, Charlotte, Greenville) */
    const untouched = e.seller ? e.lv.constr : e.lv.seller;
    /* …and when the edited half WAS "N/A" in the copy, the bundled line holds none of it,
       so it is all the untouched half (Carolinas: NC gains a construction line) */
    const wasNone = e.sn ? (e.seller ? e.sn.seller : e.sn.constr) === "" : false;
    const parts = untouched === "" ? (e.seller ? { seller: bundled, constr: "" } : { seller: "", constr: bundled })
      : wasNone ? (e.seller ? { seller: "", constr: bundled } : { seller: bundled, constr: "" })
      : splitBundled(bundled);
    if (!parts) return bundled;
    seller = e.seller ? e.lv.seller : parts.seller;
    constr = e.constr ? e.lv.constr : parts.constr;
  }
  if (seller === "?" || constr === "?") return bundled;
  return [seller, constr].filter(Boolean).join(" ").trim();
}

/**
 * The states this email is about, in the order the division lists them:
 *   1. the picked communities and masterplans, from their own Airtable address;
 *   2. else the picked markets (market code → state);
 *   3. else every state the division sells in.
 * `null` = a community or masterplan was picked but its state can't be read (no /refdata, or
 * not in it) in a division that sells in more than one state. The email IS about a specific
 * place, so the market or the division would be a guess (Charlotte's market is NC; Cannon
 * Village is SC) — the placeholders stay for Digital Ops instead.
 */
export function licenseStates(
  division: string,
  picks: { communities?: string[]; masterplans?: string[]; markets?: string[] },
  ref?: RefdataLike | null,
): string[] | null {
  const d = up(division);
  const order = K.states[d] || [];
  const found: Record<string, 1> = {};
  let unread = 0;
  const add = (s?: string) => { const v = up(s || ""); if (isState(v)) { found[v] = 1; return true; } return false; };
  const look = (names: string[] | undefined, kind: "communities" | "masterplans") => {
    const ids = (ref && ref.ids && ref.ids[kind]) || {};
    const addr = (ref && ref.addr && ref.addr[kind]) || {};
    (names || []).map((n) => String(n).trim()).filter(Boolean).forEach((n) => {
      const id = ids[n]; const row = id ? addr[String(id)] : undefined;
      if (!(row && add(row.state))) unread++;
    });
  };
  look(picks.communities, "communities");
  look(picks.masterplans, "masterplans");
  /* one state is the answer whatever was picked; more than one needs every pick read */
  if (unread && order.length !== 1) return null;
  if (!Object.keys(found).length) {
    (picks.markets || []).forEach((m) => {
      const code = up(String(m).split(" - ")[0]);
      String(K.markets[d + "|" + code] || "").split(",").forEach(add);
    });
  }
  const got = Object.keys(found);
  if (!got.length) return order.slice();
  const ranked = order.filter((s) => found[s]);
  return ranked.concat(got.filter((s) => order.indexOf(s) < 0));
}

/**
 * The line for these states. `null` = this division is not in the key (the placeholders
 * stay, so nobody mistakes a guess for a filled line). `""` = the states need no line.
 * One state, or several needing the same line → that line. Several needing different
 * lines → each prefixed with its state, the way the legal builder writes Idaho + Washington.
 */
export function licenseLine(division: string, states: string[] | null, live?: LiveLicense | null): string | null {
  const d = up(division);
  if (!(d in K.states) || !states) return null;
  const sts = states.length ? states : K.states[d];
  const all = sts.map((s) => ({ s, t: lineFor(d, s, live) }));
  /* a state with no row in the key is UNKNOWN, not "needs nothing" — keep the placeholders
     (Greenville's Asheville market is NC, and the key has no Greenville NC line) */
  if (all.some((x) => x.t === undefined)) return null;
  const per = all.filter((x) => x.t) as { s: string; t: string }[];
  if (!per.length) return "";
  const uniq = per.filter((x, i) => per.findIndex((y) => y.t === x.t) === i);
  if (uniq.length === 1) return uniq[0].t;
  return uniq.map((x) => (/^[A-Z]{2}:\s/.test(x.t) ? x.t : x.s + ": " + x.t)).join(" ");
}

/** where a division-and-state row came from: "legal", "sent" (pending a Dig Ops ruling), or "" */
export function licenseSource(division: string, state: string): string {
  const r = K.lines[up(division) + "|" + up(state)];
  return r ? r.src : "";
}
